// Trail marks (TRAILS-C, owner 2026-10-07; APP_PLAYER.md "Trail marks +
// inspect"): the Swiss hiking signalisation (SN 640 829a, SAC Wanderskala)
// reproduced as REAL furniture at REAL positions —
//   · the paint marks beside every GRADED trail (a stake at the ribbon edge,
//     yellow diamond / white-red-white / white-blue-white bands),
//   · the signposts at the OSM guidepost nodes (trails/guideposts.json): one
//     yellow blade per incident graded run, tip in the mark's colours, the
//     whole blade blue for Alpinwanderweg; the location plate (name / ele);
//     each blade lists its destinations with the DIN walking time (BLADES-T:
//     derived from the walk graph, the named guideposts, gazetteer pois and
//     route ends — `planDestinations`), the run name when none qualifies.
// Untagged trails get NO mark (untagged != T1 — never invent a grade) and no
// signpost stands where OSM has no guidepost node.
//
// Also the ONE place the mark is derived (`markOf`; MAP-D R4: the official
// swissTLM3D class decides the mark, the OSM T-grade is the label —
// `gradeText` / `gradeShort`; `mtbClass` the MTB scale) and the trail index
// WaysNet keeps (`buildTrailIndex`, client/src/ways2.js) for the player map's
// trail layer, the HUD chip and inspect. `joinTrailData` stamps the optional
// MAP-D artifacts (meta.pathinfo by OSM way, meta.routes' reverse index) onto
// it before the map and the marks are built; `prepRoutes` / `prepExtra` /
// `prepAmenities` + the polyline helpers serve the map, inspect and the route
// guide (beacons.js).
//
// Light: world furniture, NOT UI — MeshBasicMaterial with vertex colours and
// the objects.js fake-lambert face shade baked into them (no runtime lights,
// tone-mapped like every lit surface). Two draw calls: marker stakes, and the
// signposts (one CanvasTexture atlas holding the texts of the posts NEAR the
// camera only; a far blade is plain).
import * as THREE from 'three';

// sRGB, SN 640 829a (APP_PLAYER §Trail marks)
export const MARK_COLOUR = { yellow: '#F2C400', red: '#D52B1E', blue: '#1F5FBF', white: '#F4F4F4' };
// the mark vocabulary: label (HUD / inspect), the band colours top -> bottom
// (the HUD swatch, the post stripes, the blade tip)
export const MARK = {
  yellow: { label: 'Wanderweg', bands: ['yellow'] },
  wrw: { label: 'Bergwanderweg', bands: ['white', 'red', 'white'] },
  wbw: { label: 'Alpinwanderweg', bands: ['white', 'blue', 'white'] },
  ferrata: { label: 'Klettersteig', bands: ['blue', 'white'] },
  none: { label: '', bands: [] },
};

// the official swissTLM3D hiking class (pathinfo `ww`, MAP-D owner ruling
// R4 "Official class decides the mark; OSM T-grade is the label")
export const OFFICIAL_MARK = { W: 'yellow', B: 'wrw', A: 'wbw' };
// an official class without an OSM sac: the class's T range (SN 640 829a)
export const CLASS_RANGE = { yellow: 'T1', wrw: 'T2–T3', wbw: 'T4–T6' };
// MTB difficulty (OSM mtb:scale, 0..6): S0 green, S1 blue, S2 red, S3+ black
export const MTB_COLOUR = { s0: '#2E9E44', s1: '#1F5FBF', s2: '#D52B1E', s3: '#151515' };
const MTB_WORD = ['easy', 'moderate', 'difficult', 'very difficult', 'extreme', 'extreme', 'extreme'];

const hasVf = (run) => !!(run.tt && run.tt.vf !== undefined && run.tt.vf !== null && run.tt.vf !== '');

// run (a ways.json run or a trail index entry) -> mark key. A via ferrata
// (tt.vf, or the official object class 'ferrata') is its own treatment,
// never a blue Alpinwanderweg; then the OFFICIAL class (pathinfo `off`, R4);
// then the SAC grade; anything else (sac null: untagged, or a pathinfo extra)
// -> 'none'. Without pathinfo fields this is exactly the TRAILS-C rule.
export function markOf(run) {
  if (!run) return 'none';
  if (hasVf(run) || run.oa === 'ferrata') return 'ferrata';
  const off = run.off && OFFICIAL_MARK[run.off];
  if (off) return off;
  const s = run.sac;
  if (s === 1) return 'yellow';
  if (s === 2 || s === 3) return 'wrw';
  if (s >= 4 && s <= 6) return 'wbw';
  return 'none';
}

// the short grade: 'T3' (OSM sac) / 'K4' (via ferrata) / '' — the map's
// grade chips; an official class without sac has no T of its own
export function gradeShort(t) {
  if (!t) return '';
  const m = t.mark || markOf(t);
  if (m === 'ferrata') {
    if (!hasVf(t)) return 'K';
    const vf = String(t.tt.vf);
    return /^\d+$/.test(vf) ? 'K' + vf : vf;
  }
  return Number.isInteger(t.sac) && t.sac >= 1 && t.sac <= 6 ? `T${t.sac}` : '';
}

// 'Bergwanderweg · T3' / 'Klettersteig · K4' / 'Bergwanderweg · T2–T3' (an
// official class, no sac: the class range) / '' (ungraded)
export function gradeText(t) {
  const m = t && (t.mark || markOf(t));
  if (!m || m === 'none') return '';
  const g = gradeShort(t);
  if (m === 'ferrata') return g && g !== 'K' ? `${MARK.ferrata.label} · ${g}` : MARK.ferrata.label;
  const T = g || (t.off ? CLASS_RANGE[m] : '');
  return T ? `${MARK[m].label} · ${T}` : MARK[m].label;
}

// OSM mtb:scale ('0'..'6', '2+', '3-') -> {n, key, label 'S2', colour, word} | null
export function mtbClass(v) {
  if (v === undefined || v === null || v === '') return null;
  const m = /^\s*(\d)/.exec(String(v));
  if (!m) return null;
  const n = Number(m[1]);
  if (n > 6) return null;
  const key = n >= 3 ? 's3' : 's' + n;
  return { n, key, label: 'S' + String(v).trim(), colour: MTB_COLOUR[key], word: MTB_WORD[n] };
}

// ------------------------------------------------- MAP-D artifacts (join)
// meta.pathinfo (vrm-pathinfo), meta.routes (vrm-routes): every one optional.
// joinTrailData(trails, pathinfo, routes) stamps the trail index entries in
// place — {off (W|B|A|null), oa, bel, cov, fahr} by the run's OSM `way`, the
// mark recomputed (R4), and `routes` = [route index…] (the reverse index) —
// BEFORE PlayerMap.setTrails / TrailMarks.build. → stats
export function routeIndex(routesDoc) {
  const byWay = new Map();
  const R = routesDoc && Array.isArray(routesDoc.routes) ? routesDoc.routes : [];
  R.forEach((r, i) => {
    for (const w of r.ways || []) {
      const k = String(w);
      let a = byWay.get(k);
      if (!a) byWay.set(k, a = []);
      if (a[a.length - 1] !== i) a.push(i);
    }
  });
  return byWay;
}

export function joinTrailData(trails, pathinfo, routesDoc) {
  const st = { pathinfo: !!pathinfo, matched: 0, official: 0, remarked: 0, routes: 0, on_routes: 0 };
  const ways = pathinfo && pathinfo.ways && typeof pathinfo.ways === 'object' ? pathinfo.ways : null;
  const byWay = routesDoc ? routeIndex(routesDoc) : null;
  if (byWay) st.routes = routesDoc.routes.length;
  for (const t of trails || []) {
    if (t.way === null || t.way === undefined) continue;
    const k = String(t.way);
    const p = ways ? ways[k] : null;
    if (p) {
      t.off = OFFICIAL_MARK[p.ww] ? p.ww : null;
      t.oa = p.oa || null;
      t.bel = p.bel || null;
      t.cov = Number.isFinite(p.cov) ? p.cov : null;
      if (p.fahr) t.fahr = String(p.fahr);
      st.matched++;
      if (t.off) st.official++;
      const m = markOf(t);
      if (m !== t.mark) { t.mark = m; st.remarked++; }
    }
    const r = byWay && byWay.get(k);
    if (r) { t.routes = r; st.on_routes++; }
  }
  return st;
}

// ------------------------------------------------------ polyline helpers
// interleaved xz Float32Array / number[] → cumulative arc length per vertex
export function polyArc(P) {
  const n = P.length >> 1, a = new Float64Array(Math.max(1, n));
  for (let i = 1; i < n; i++) a[i] = a[i - 1] + Math.hypot(P[2 * i] - P[2 * i - 2], P[2 * i + 1] - P[2 * i - 1]);
  return a;
}
// the point at arc length s (clamped to [0, L]) → [x, z, ux, uz] (unit tangent)
export function pointAtArc(P, A, s) {
  const n = P.length >> 1;
  if (n < 2) return [P[0], P[1], 1, 0];
  const L = A[n - 1];
  s = Math.max(0, Math.min(L, s));
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) { const m = (lo + hi) >> 1; if (A[m] <= s) lo = m; else hi = m; }
  const seg = A[hi] - A[lo], u = seg > 1e-9 ? (s - A[lo]) / seg : 0;
  const dx = P[2 * hi] - P[2 * lo], dz = P[2 * hi + 1] - P[2 * lo + 1], d = Math.hypot(dx, dz) || 1;
  return [P[2 * lo] + dx * u, P[2 * lo + 1] + dz * u, dx / d, dz / d];
}
// (x, z) projected on the polyline, only segments overlapping arc [lo, hi]
// → {s, d, x, z} (ties: the lower arc wins — a loop's start, not its end)
export function projectOnPoly(P, A, x, z, lo = -Infinity, hi = Infinity) {
  const n = P.length >> 1;
  let best = null;
  for (let i = 0; i + 1 < n; i++) {
    if (A[i + 1] < lo || A[i] > hi) continue;
    const ax = P[2 * i], az = P[2 * i + 1], dx = P[2 * i + 2] - ax, dz = P[2 * i + 3] - az;
    const L2 = dx * dx + dz * dz;
    const u = L2 > 1e-12 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2)) : 0;
    const px = ax + dx * u, pz = az + dz * u, d = Math.hypot(x - px, z - pz);
    if (!best || d < best.d - 1e-9) best = { s: A[i] + (A[i + 1] - A[i]) * u, d, x: px, z: pz };
  }
  return best;
}
// the nearest item {pts (xz interleaved), bbox} of a list to (x, z) within r → {item, d, x, z} | null
export function nearestPoly(list, x, z, r) {
  let best = null;
  for (const t of list || []) {
    const b = t.bbox, P = t.pts;
    if (!P || P.length < 4 || (b && (x < b[0] - r || x > b[2] + r || z < b[1] - r || z > b[3] + r))) continue;
    for (let i = 0; i + 3 < P.length; i += 2) {
      const ax = P[i], az = P[i + 1], dx = P[i + 2] - ax, dz = P[i + 3] - az, L2 = dx * dx + dz * dz;
      const u = L2 > 1e-12 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2)) : 0;
      const px = ax + dx * u, pz = az + dz * u, d = Math.hypot(x - px, z - pz);
      if (d <= r && (!best || d < best.d)) best = { item: t, d, x: px, z: pz };
    }
  }
  return best;
}
const bboxOfPts = (P) => {
  const b = [Infinity, Infinity, -Infinity, -Infinity];
  for (let i = 0; i + 1 < P.length; i += 2) {
    if (P[i] < b[0]) b[0] = P[i]; if (P[i] > b[2]) b[2] = P[i];
    if (P[i + 1] < b[1]) b[1] = P[i + 1]; if (P[i + 1] > b[3]) b[3] = P[i + 1];
  }
  return b;
};

// meta.routes doc → the routes list with every stage made drawable:
// stage {pts Float32Array, arc, len, bbox}, route.bbox; bad entries dropped.
// null when the doc is not a vrm-routes doc.
export function prepRoutes(doc) {
  if (!doc || !Array.isArray(doc.routes)) return null;
  const out = [];
  for (const r of doc.routes) {
    if (!r || !Array.isArray(r.stages)) continue;
    const stages = [];
    for (const s of r.stages) {
      if (!s || !Array.isArray(s.pts) || s.pts.length < 4) continue;
      const pts = Float32Array.from(s.pts);
      const arc = polyArc(pts);
      stages.push({ ...s, pts, arc, len: arc[arc.length - 1], bbox: bboxOfPts(pts) });
    }
    if (!stages.length) continue;
    const bbox = stages.reduce((b, s) => [Math.min(b[0], s.bbox[0]), Math.min(b[1], s.bbox[1]), Math.max(b[2], s.bbox[2]), Math.max(b[3], s.bbox[3])],
      [Infinity, Infinity, -Infinity, -Infinity]);
    out.push({ ...r, stages, bbox });
  }
  return out;
}

// meta.pathinfo extra (TLM-only official segments, R5: map-only, tappable,
// never walkable) → [{ww, oa, bel, mark, pts, bbox, len_m, extra: true}]
export function prepExtra(pathinfo) {
  const E = pathinfo && Array.isArray(pathinfo.extra) ? pathinfo.extra : [];
  const out = [];
  for (const e of E) {
    if (!e || !OFFICIAL_MARK[e.ww] || !Array.isArray(e.pts) || e.pts.length < 4) continue;
    const pts = Float32Array.from(e.pts), arc = polyArc(pts);
    const t = { off: e.ww, oa: e.oa || null, bel: e.bel || null, sac: null, tt: null, pts, bbox: bboxOfPts(pts),
      len_m: Math.round(arc[arc.length - 1]), extra: true, name: '' };
    t.mark = markOf(t);
    out.push(t);
  }
  return out;
}

// meta.amenities doc → the valid points [{k, x, z, y, name, ele, t}] (null: not a doc);
// DETAIL-1 `street` (bus stops, point barriers) AFTER `pts`, so a pts index never moves
export function prepAmenities(doc) {
  if (!doc || !Array.isArray(doc.pts)) return null;
  const ok = (p) => p && typeof p.k === 'string' && Number.isFinite(p.x) && Number.isFinite(p.z);
  return doc.pts.filter(ok).concat(Array.isArray(doc.street) ? doc.street.filter(ok) : []);
}

// ------------------------------------------------------------ trail index
const TRAIL_CLS = new Set(['path', 'stairs']);   // render cls of the path family (pcls path / track / alpine / steps)
const DECIM_M = 2;                              // map polyline chord error

// Douglas-Peucker on xz: kept station indices (ends always kept)
function decimate(X, Z, tol) {
  const n = X.length, keep = new Uint8Array(n);
  keep[0] = keep[n - 1] = 1;
  const st = [[0, n - 1]];
  const t2 = tol * tol;
  while (st.length) {
    const [a, b] = st.pop();
    if (b - a < 2) continue;
    const ax = X[a], az = Z[a], dx = X[b] - ax, dz = Z[b] - az, L2 = dx * dx + dz * dz;
    let best = -1, bd = t2;
    for (let i = a + 1; i < b; i++) {
      const px = X[i] - ax, pz = Z[i] - az;
      let d2;
      if (L2 < 1e-9) d2 = px * px + pz * pz;
      else {
        const c = px * dz - pz * dx;
        d2 = (c * c) / L2;
        const u = (px * dx + pz * dz) / L2;
        if (u < 0) d2 = px * px + pz * pz;
        else if (u > 1) { const qx = X[i] - X[b], qz = Z[i] - Z[b]; d2 = qx * qx + qz * qz; }
      }
      if (d2 > bd) { bd = d2; best = i; }
    }
    if (best > 0) { keep[best] = 1; st.push([a, best], [best, b]); }
  }
  const out = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(i);
  return out;
}

// ways.json doc -> { trails: [...], byRun: Map(run -> trail), stats }.
// Per path-family run {run, chain, way (the OSM way id: the pathinfo / routes
// join key), name, pcls, sac, mark, tt, pts (xz Float32Array, interleaved,
// decimated <= 2 m chord), len_m (xz arc), asc_m, desc_m, bbox [x0, z0, x1,
// z1]} + `st` = the station arrays {x, z, y, kind} for the marker posts and
// the trail lines (TrailMarks.build drops `st` once it has placed them; the
// lines capture it first). Every path run carries `way`, `sac` (null =
// untagged) and per-station `kind` (world/profile.js trailOf) — a run without
// them is a broken bake and throws.
export function buildTrailIndex(doc) {
  const trails = [], byRun = new Map();
  const stats = { runs: 0, graded: 0, marks: { yellow: 0, wrw: 0, wbw: 0, ferrata: 0, none: 0 }, pts: 0, stations: 0, bytes: 0 };
  const W = (doc && doc.ways) || [];
  for (let r = 0; r < W.length; r++) {
    const e = W[r];
    if (!TRAIL_CLS.has(e.cls) || !Array.isArray(e.x) || e.x.length < 2) continue;
    if (!('sac' in e) || !('way' in e) || !Array.isArray(e.kind) || e.kind.length !== e.x.length) {
      throw new Error(`world ways.json run ${r}: sac / way / kind missing (a path run without its trail fields) — rebuild this world (Build)`);
    }
    const n = e.x.length, X = e.x, Z = e.z, Y = e.y;
    let len = 0, asc = 0, desc = 0;
    for (let i = 1; i < n; i++) {
      len += Math.hypot(X[i] - X[i - 1], Z[i] - Z[i - 1]);
      const dy = Y[i] - Y[i - 1];
      if (dy > 0) asc += dy; else desc -= dy;
    }
    const keep = decimate(X, Z, DECIM_M);
    const pts = new Float32Array(keep.length * 2);
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    keep.forEach((i, k) => {
      pts[2 * k] = X[i]; pts[2 * k + 1] = Z[i];
      if (X[i] < x0) x0 = X[i]; if (X[i] > x1) x1 = X[i];
      if (Z[i] < z0) z0 = Z[i]; if (Z[i] > z1) z1 = Z[i];
    });
    const way = e.way;
    const t = { run: r, chain: e.chain, way, name: e.name || '', pcls: e.pcls, sac: Number.isInteger(e.sac) ? e.sac : null,
      mark: 'none', tt: e.tt || null, pts, len_m: Math.round(len), asc_m: Math.round(asc), desc_m: Math.round(desc),
      bbox: [x0, z0, x1, z1], w: e.w || 1.5 };
    t.mark = markOf(t);
    t.st = { x: Float32Array.from(X), z: Float32Array.from(Z), y: Float32Array.from(Y), kind: Uint8Array.from(e.kind) };
    if (t.mark !== 'none') stats.graded++;
    stats.stations += n;
    stats.bytes += n * 13;
    stats.marks[t.mark]++;
    stats.runs++;
    stats.pts += keep.length;
    stats.bytes += pts.byteLength + 160 + (t.name.length * 2);
    trails.push(t);
    byRun.set(r, t);
  }
  return { trails, byRun, stats };
}

// nearest path-family trail to (x, y, z) through the RoadGraph (its ways
// carry `run`, game/waysdraw.js graphInput): within r horizontally, dy
// vertically (a deck over a path). null when none.
export function trailNear(graph, byRun, x, y, z, r = 8, dy = 6) {
  if (!graph || !byRun || !byRun.size) return null;
  let best = null, bd = Infinity;
  graph.near(x, y, z, r, dy, (wi, pi, d2) => {
    if (d2 >= bd) return;
    const t = byRun.get(graph.ways[wi].run);
    if (t) { bd = d2; best = t; }
  });
  return best;
}

// ---------------------------------------------------------------- geometry
const lin = (hex) => { const c = new THREE.Color(hex); return [c.r, c.g, c.b]; };   // sRGB hex -> linear working space
const COL = {
  yellow: lin(MARK_COLOUR.yellow), red: lin(MARK_COLOUR.red), blue: lin(MARK_COLOUR.blue), white: lin(MARK_COLOUR.white),
  wood: lin('#7a5c3e'), pole: lin('#9a9ea4'),
};

export const POST = {
  spacing_m: 80, spacing_poor_m: 40, spacing_good_m: 120,   // tt.vis bad|horrible|no / excellent
  edge_m: 0.3,               // past the ribbon edge (w/2 + edge_m)
  h_m: 1.4, sink_m: 0.4,     // stake over / under the station y (no heightfield lookup)
  half_m: 0.05,              // stake half width (10 cm square)
  band_m: 0.12,              // one stripe
  plate_m: 0.09,             // diamond half diagonal / ferrata plate half size
  proud_m: 0.012,            // plate off the stake face
  tri_budget: 50000,
};
export const SIGN = {
  radius_m: 15,              // a graded run this near a guidepost gets its blade(s)
  reach_m: 12, min_m: 6,     // tangent chord; a direction needs this much way
  pole_h_m: 2.45, pole_half_m: 0.04, sink_m: 0.3,
  blade_l_m: 0.72, tip_m: 0.12, point_m: 0.1,
  blade_h_m: 0.135,          // body height, 1–2 lines (the TRAILS-C blade)
  blade_h3_m: 0.18,          // 3 destination lines (= slot_h / slot_w × blade_l_m)
  line_m: 0.045,             // destination line pitch
  blade_top_m: 2.18, blade_gap_m: 0.055, max_blades: 6, merge_deg: 20,   // first blade's centre; gap under each blade
  plate_l_m: 0.42, plate_h_m: 0.07875,
  // the near-only text atlas: 1024² (4 MB), 3 × 12 slots of 340 × 85 texels
  // (472 texels / m on the blade), slot 0 white = 35 text slots
  slot_w: 340, slot_h: 85, atlas_w: 1024, atlas_h: 1024,
  near_m: 60, far_m: 75, move_m: 15,   // a post's texts load within near_m, unload past far_m; re-checked per move_m of camera
  tri_budget: 20000,
};

// objects.js Acc: fake-lambert per face (brighter tops, sides keyed off the
// normal) — the same constants, so a stake reads like the decor beside it
function shade(nx, ny, nz, c) {
  const s = 0.68 + 0.3 * Math.max(0, ny) + 0.09 * nx + 0.05 * nz;
  return [c[0] * s, c[1] * s, c[2] * s];
}

class Acc {
  constructor(uv) { this.pos = []; this.col = []; this.uv = uv ? [] : null; this.idx = []; }
  get tris() { return this.idx.length / 3; }
  // a planar polygon (fan), normal n, colour c, optional per-vertex uv →
  // its first vertex index
  poly(P, n, c, U = null) {
    const cc = shade(n[0], n[1], n[2], c);
    const base = this.pos.length / 3;
    P.forEach((p, k) => {
      this.pos.push(p[0], p[1], p[2]); this.col.push(cc[0], cc[1], cc[2]);
      if (this.uv) { const u = U ? U[k] : WHITE_UV; this.uv.push(u[0], u[1]); }
    });
    for (let k = 1; k + 1 < P.length; k++) this.idx.push(base, base + k, base + k + 1);
    return base;
  }
  geometry() {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    if (this.uv) g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    g.setIndex(this.idx);
    g.computeBoundingSphere();
    return g;
  }
}

// a vertical square prism (sides + top) centred on (x, z), faces along f / r
function prism(A, x, z, y0, y1, h, f, c) {
  const r = [-f[1], f[0]];
  const pt = (a, b, y) => [x + f[0] * a + r[0] * b, y, z + f[1] * a + r[1] * b];
  const C = [[-h, -h], [h, -h], [h, h], [-h, h]];   // corners in (along f, along r)
  const N = [[-r[0], -r[1]], f, r, [-f[0], -f[1]]]; // face k = corners k -> k+1
  for (let k = 0; k < 4; k++) {
    const a = C[k], b = C[(k + 1) % 4];
    A.poly([pt(a[0], a[1], y0), pt(b[0], b[1], y0), pt(b[0], b[1], y1), pt(a[0], a[1], y1)], [N[k][0], 0, N[k][1]], c);
  }
  A.poly(C.map((q) => pt(q[0], q[1], y1)), [0, 1, 0], c);
}

// a deterministic PRNG per run (mulberry32)
function rng(seed) {
  let a = (seed * 2654435761) >>> 0 ^ 0x9e3779b9;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const spacingOf = (t) => {
  const v = t.tt && t.tt.vis;
  return v === 'bad' || v === 'horrible' || v === 'no' ? POST.spacing_poor_m : v === 'excellent' ? POST.spacing_good_m : POST.spacing_m;
};
const TRIS_PER_POST = { yellow: 14, wrw: 22, wbw: 22, ferrata: 18 };

// the marker stakes of every graded trail: [{x, y, z, f:[fx, fz], mark}]
// along the surface stations every spacing m (seeded start / side per run),
// alternating sides at w/2 + edge_m. Spacing scales up uniformly when the
// set would pass POST.tri_budget. planPostsSteps = THE plan, a step per
// `every` graded runs (M7c: one 33-50 ms task after the Beam); planPosts =
// its drain
export function planPosts(trails) {
  const it = planPostsSteps(trails);
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}
export function* planPostsSteps(trails, every = 16) {
  const graded = trails.filter((t) => t.mark !== 'none' && t.st);
  let est = 0;
  for (const t of graded) est += (t.len_m / spacingOf(t) + 1) * TRIS_PER_POST[t.mark];
  const k = Math.max(1, est / POST.tri_budget);
  const out = [];
  for (let ti = 0; ti < graded.length; ti++) {
    if (ti && ti % every === 0) yield;
    const t = graded[ti];
    const { x: X, z: Z, y: Y, kind: K } = t.st;
    const n = X.length, R = rng(t.run + 1), sp = spacingOf(t) * k;
    let side = R() < 0.5 ? -1 : 1;
    const off = t.w / 2 + POST.edge_m;
    let s0 = 0, target = R() * sp;
    for (let i = 0; i + 1 < n; i++) {
      const dx = X[i + 1] - X[i], dz = Z[i + 1] - Z[i], L = Math.hypot(dx, dz);
      while (L > 1e-6 && target <= s0 + L) {
        if (!(K[i] || K[i + 1])) {   // surface stations only (no post on a deck / in a bore)
          const u = (target - s0) / L, fx = dx / L, fz = dz / L;
          out.push({ x: X[i] + dx * u - fz * off * side, y: Y[i] + (Y[i + 1] - Y[i]) * u,
            z: Z[i] + dz * u + fx * off * side, f: [fx, fz], mark: t.mark });
          side = -side;
        }
        target += sp;
      }
      s0 += L;
    }
  }
  return { posts: out, spacing_scale: k };
}

// one stake: wood, the two walker-facing faces (±f) carry the mark
function postGeo(A, p) {
  const P = POST, h = P.half_m, f = p.f, y0 = p.y - P.sink_m, y1 = p.y + P.h_m;
  const r = [-f[1], f[0]];
  const pt = (a, b, y) => [p.x + f[0] * a + r[0] * b, y, p.z + f[1] * a + r[1] * b];
  // wood sides (±r) + top
  for (const sb of [1, -1]) A.poly([pt(sb * h, sb * h, y0), pt(-sb * h, sb * h, y0), pt(-sb * h, sb * h, y1), pt(sb * h, sb * h, y1)], [r[0] * sb, 0, r[1] * sb], COL.wood);
  A.poly([pt(-h, -h, y1), pt(-h, h, y1), pt(h, h, y1), pt(h, -h, y1)], [0, 1, 0], COL.wood);
  for (const sa of [1, -1]) {
    const n = [f[0] * sa, 0, f[1] * sa];
    const face = (ya, yb, c) => A.poly([pt(sa * h, -sa * h, ya), pt(sa * h, sa * h, ya), pt(sa * h, sa * h, yb), pt(sa * h, -sa * h, yb)], n, c);
    if (p.mark === 'wrw' || p.mark === 'wbw') {
      // painted bands round the stake head: white / red|blue / white, top down
      const b = P.band_m, acc = p.mark === 'wrw' ? COL.red : COL.blue;
      face(y0, y1 - 3 * b, COL.wood);
      face(y1 - 3 * b, y1 - 2 * b, COL.white); face(y1 - 2 * b, y1 - b, acc); face(y1 - b, y1, COL.white);
    } else {
      face(y0, y1, COL.wood);
      const a = sa * (h + P.proud_m), yc = y1 - 0.2, d = P.plate_m;
      if (p.mark === 'yellow') {
        A.poly([pt(a, -sa * d, yc), pt(a, 0, yc - d), pt(a, sa * d, yc), pt(a, 0, yc + d)], n, COL.yellow);
      } else {   // ferrata: a small blue-over-white plate
        const q = d * 0.75;
        A.poly([pt(a, -sa * q, yc), pt(a, sa * q, yc), pt(a, sa * q, yc + q), pt(a, -sa * q, yc + q)], n, COL.blue);
        A.poly([pt(a, -sa * q, yc - q), pt(a, sa * q, yc - q), pt(a, sa * q, yc), pt(a, -sa * q, yc)], n, COL.white);
      }
    }
  }
}

// ------------------------------------------------------ blade destinations
// (BLADES-T, owner 2026-10-08 "the way markers have the correct text") A
// Swiss direction blade (SN 640 829a) lists destinations with the walking
// time: the next intermediate one, optionally one mid, and a far / final
// one, nearest on top. Derived once in `build` from data already loaded:
// the walk graph (RoadGraph ways, junctions joined), other named guideposts,
// the gazetteer pois (optional) and the route relations' from / to
// (optional). A blade with nothing that qualifies keeps the run name.

// DIN 33466 (the SAC / Wanderwege rule on every yellow signpost): 4 km/h
// horizontal, 300 m/h up, 500 m/h down, t = max + min / 2 → h. The same
// formula as inspect.js walkTimeH (duplicated: inspect.js imports this module).
export function walkTimeH(lenM, ascM, descM) {
  const th = (lenM / 1000) / 4;
  const tv = ascM / 300 + descM / 500;
  return Math.max(th, tv) + Math.min(th, tv) / 2;
}
// minutes → signpost text, rounded to 5 min: "45 min" / "1 h 30 min" /
// "2 h 05 min" (= inspect.js fmtHours)
export function fmtWalkMin(min) {
  const m = Math.round(min / 5) * 5;
  const hh = Math.floor(m / 60), mm = m % 60;
  return hh ? `${hh} h${mm ? ` ${String(mm).padStart(2, '0')} min` : ''}` : `${mm} min`;
}

export const DEST = {
  min_min: 10, max_min: 300,   // a line's walking time window (raw minutes)
  sep_min: 15,                 // near / mid / final at least this far apart
  decay_min: 90,               // the final: weight − minutes / decay_min (a town 4 h away loses to a village 1 h away)
  max_lines: 3,
  attach_m: 40, attach_dy_m: 40,   // a poi / route end this near a walkable vertex
  here_m: 100,                 // a candidate this near the post is "here", not a destination
  link_m: 2.5, link_dy_m: 3,   // a way end this near another way's vertex is a junction
  gap_max_m: 2000,             // one run's pieces either side of a deck / bore are joined
  isolated_m: 1200, cluster_m: 300,   // a restaurant / hotel counts when isolated
  mountain_m: 150, valley_r_m: 3000, valley_cell_m: 250,   // … and a MOUNTAIN place: ≥ mountain_m over the valley (the lowest walk vertex ≈ valley_r_m around)
  clip_m: 150,                 // a route end this near the network's bbox edge is a clip
};
// destination weight (the final / mid pick): settlements first; a named
// guidepost is an intermediate (or its settlement when its name starts with
// one: "Samedan Staziun"); a route end on a blade that follows the route wins
const IMP = { town: 6, village: 5, suburb: 4, hamlet: 4, alpine_hut: 4, wilderness_hut: 3, station: 3, pass: 3, saddle: 3, peak: 3 };
const SETTLE = new Set(['town', 'village', 'suburb', 'hamlet']);
const ISOLATED = new Set(['restaurant', 'hotel', 'hostel', 'cafe', 'guest_house', 'chalet']);
const BUILT = new Set(['restaurant', 'hotel', 'hostel', 'cafe', 'bar', 'guest_house', 'chalet', 'museum', 'church', 'gallery', 'attraction', 'hospital', 'civic', 'public']);
const IMP_GP = 1, IMP_ISO = 2, IMP_MAJOR = 3, IMP_ROUTE = 7;

// a display name: pictographs dropped ("St. Moritz Bad 🚌"), spaces collapsed
export function cleanName(s) {
  return String(s || '').replace(/\p{Extended_Pictographic}|\uFE0F|\u200D/gu, '').replace(/\s+/g, ' ').trim();
}
// the comparison key: case, hyphen / space and the Romansh hut abbreviation
// ("Chna. Segantini" = "Chamanna Segantini") folded
const keyOf = (s) => s.toLocaleLowerCase('de-CH').replace(/\bchna\.\s*/g, 'chamanna ').replace(/[\s\-–]+/g, ' ').trim();

// guideposts (the parsed doc's posts), pois (gazetteer pois: {kind, name, x,
// z, ele}), routes (prepRoutes' list) → candidates [{name, key, grp, x, z, y, imp,
// src 'gp'|'poi'|'route', ri, r, dy, iso}]; bbox = the walk network's [x0,
// z0, x1, z1]. A generator (a yield between the phases — destJob steps it)
function* destCandidatesJob(gps, pois, routes, bbox) {
  const out = [];
  // only the kinds that matter: destinations (IMP / ISOLATED) and the
  // buildings of the cluster test (the gazetteer's far peaks etc. skipped)
  const P = Array.isArray(pois) ? pois.filter((p) => p && (IMP[p.kind] || ISOLATED.has(p.kind) || BUILT.has(p.kind))
    && Number.isFinite(p.x) && Number.isFinite(p.z) && cleanName(p.name)) : [];
  const built = P.filter((p) => BUILT.has(p.kind));
  const settle = P.filter((p) => SETTLE.has(p.kind));
  const settleKeys = settle.map((p) => keyOf(cleanName(p.name))).sort((a, b) => b.length - a.length || (a < b ? -1 : 1));
  // the settlement a name belongs to ("Samedan Staziun" → samedan): its
  // weight, and the group no two lines of one blade share
  const settleOf = (k) => {
    for (const s of settleKeys) if (k === s || ' /,'.includes(k.charAt(s.length)) && k.startsWith(s)) {
      const p = settle.find((q) => keyOf(cleanName(q.name)) === s);
      return { imp: IMP[p.kind] || IMP_MAJOR, grp: s };
    }
    return { imp: 0, grp: k };
  };
  for (const g of gps || []) {
    const name = cleanName(g && g.name);
    if (!name || !Number.isFinite(g.x) || !Number.isFinite(g.z)) continue;
    const key = keyOf(name), so = settleOf(key);
    out.push({ name, key, grp: so.grp, x: g.x, z: g.z, y: Number.isFinite(g.y) ? g.y : NaN, imp: Math.max(IMP_GP, so.imp), src: 'gp', ri: -1, r: SIGN.radius_m, dy: 25 });
  }
  yield;
  for (const p of P) {
    const name = cleanName(p.name), key = keyOf(name);
    let imp = IMP[p.kind] || 0;
    if (!imp && ISOLATED.has(p.kind)) {
      const d2 = (q) => (q.x - p.x) * (q.x - p.x) + (q.z - p.z) * (q.z - p.z);
      const lone = !settle.some((s) => d2(s) < DEST.isolated_m ** 2)
        && !built.some((q) => q !== p && d2(q) < DEST.cluster_m ** 2);
      if (lone) imp = IMP_ISO;
    }
    if (!imp) continue;
    const so = settleOf(key);
    out.push({ name, key, grp: so.grp, x: p.x, z: p.z, y: Number.isFinite(p.ele) ? p.ele : NaN, imp: Math.max(imp, so.imp), src: 'poi', ri: -1, r: DEST.attach_m, dy: DEST.attach_dy_m,
      iso: ISOLATED.has(p.kind) });   // iso: the mountain test once attached (destJob)
  }
  yield;
  // route ends: the relation's from / to (else a "A - B" name), each at the
  // end nearer a same-named candidate (else stage order); a clipped end
  // (near the network's edge: the route goes on outside the map) is no end
  (routes || []).forEach((r, ri) => {
    if (!r || !/^(hiking|foot)$/.test(r.route) || !r.stages || !r.stages.length) return;
    let from = cleanName(r.from), to = cleanName(r.to);
    if (!from && !to) {
      const m = /^(.+?)\s+[-–]\s+(.+)$/.exec(cleanName(r.name));
      if (m) { from = m[1]; to = m[2]; }
    }
    if (!from && !to) return;
    const s0 = r.stages[0].pts, sl = r.stages[r.stages.length - 1].pts;
    const A = [s0[0], s0[1]], B = [sl[sl.length - 2], sl[sl.length - 1]];
    if (Math.hypot(A[0] - B[0], A[1] - B[1]) < 2 * DEST.here_m) return;   // a loop has no ends
    const dTo = (name, e) => {
      const k = keyOf(name);
      let d = Infinity;
      for (const c of out) if (c.src !== 'route' && c.key === k) d = Math.min(d, Math.hypot(c.x - e[0], c.z - e[1]));
      return d;
    };
    let ends = [[from, A], [to, B]];
    if (Math.min(dTo(from, B), dTo(to, A)) < Math.min(dTo(from, A), dTo(to, B))) ends = [[from, B], [to, A]];
    for (const [name, e] of ends) {
      if (!name) continue;
      if (bbox && (e[0] - bbox[0] < DEST.clip_m || bbox[2] - e[0] < DEST.clip_m || e[1] - bbox[1] < DEST.clip_m || bbox[3] - e[1] < DEST.clip_m)) continue;
      const key = keyOf(name);
      out.push({ name, key, grp: settleOf(key).grp, x: e[0], z: e[1], y: NaN, imp: IMP_ROUTE, src: 'route', ri, r: DEST.attach_m, dy: 1e4 });
    }
  });
  return out;
}

// The walk graph over RoadGraph's non-rail ways (roads + paths): nodes at
// way ends, junctions (a way end within link_m of another way's vertex —
// end-to-end joins merge into one node), and the extra cut vertices asked
// for (guidepost starts, candidate attach points); one edge per stretch
// between cuts with its DIN minutes both ways (from the way's own y); a run
// cut by a deck / bore is joined across it. CSR arrays, built once.
// new WalkNet(graph, want) builds at once; new WalkNet(graph, want, true)
// leaves it to `job` (a generator: each step ≤ ~2 ms of work, the caller
// drives it — destJob does, so no long task while the player is in the world)
export class WalkNet {
  constructor(graph, want = [], deferred = false) {
    this.N = 0; this.edges = 0;
    this.job = this._build(graph, want);
    if (!deferred) while (!this.job.next().done);
  }
  *_build(graph, want) {
    const W = graph.ways, n = W.length;
    let work = 0;   // vertices / probes since the last yield
    const STEP = 20000;
    const ok = (wi) => W[wi].kind !== 'rail';
    const cuts = new Array(n);
    for (let wi = 0; wi < n; wi++) if (ok(wi)) cuts[wi] = new Set([0, W[wi].pts.length - 1]);
    yield;
    const links = [];
    const best = new Map();
    for (let wi = 0; wi < n; wi++) {
      if (!ok(wi)) continue;
      const P = W[wi].pts;
      for (const pe of [0, P.length - 1]) {
        best.clear();
        const p = P[pe];
        graph.near(p[0], p[1], p[2], DEST.link_m, DEST.link_dy_m, (wj, pj, d2) => {
          if (wj === wi || !ok(wj)) return;
          const b = best.get(wj);
          if (!b || d2 < b.d2 || (d2 === b.d2 && pj < b.pj)) best.set(wj, { pj, d2 });
        });
        for (const [wj, b] of best) { cuts[wj].add(b.pj); links.push(wi, pe, wj, b.pj); }
      }
      if ((work += 40) > STEP) { work = 0; yield; }
    }
    for (const [wi, pi] of want) if (cuts[wi]) cuts[wi].add(pi);
    // node ids: (way, cut) in way order
    const c0 = new Int32Array(n + 1);
    for (let wi = 0; wi < n; wi++) c0[wi + 1] = c0[wi] + (cuts[wi] ? cuts[wi].size : 0);
    const cutPi = new Int32Array(c0[n]);
    for (let wi = 0; wi < n; wi++) {
      if (cuts[wi]) [...cuts[wi]].sort((a, b) => a - b).forEach((pi, k) => { cutPi[c0[wi] + k] = pi; });
      if ((wi & 2047) === 2047) yield;
    }
    this.c0 = c0; this.cutPi = cutPi;
    yield;
    // junctions: union the linked cuts into one node
    const par = new Int32Array(c0[n]).map((_, i) => i);
    const find = (i) => { while (par[i] !== i) { par[i] = par[par[i]]; i = par[i]; } return i; };
    for (let k = 0; k < links.length; k += 4) {
      const a = find(this.cutAt(links[k], links[k + 1])), b = find(this.cutAt(links[k + 2], links[k + 3]));
      if (a !== b) par[Math.max(a, b)] = Math.min(a, b);
    }
    yield;
    const root = new Int32Array(c0[n]);
    const id = new Int32Array(c0[n]).fill(-1);
    let N = 0;
    for (let i = 0; i < c0[n]; i++) { const r = find(i); if (id[r] < 0) id[r] = N++; root[i] = id[r]; }
    this.nodeOfCut = root; this.N = N;
    yield;
    // edges: consecutive cuts per way (segFw[k] = cut k → k+1, segBw[k] = k+1 → k)
    const segFw = new Float32Array(c0[n]), segBw = new Float32Array(c0[n]);
    const A = [], B = [], C = [];
    const arc = (u, v, w) => { if (u !== v) { A.push(u); B.push(v); C.push(w); } };
    for (let wi = 0; wi < n; wi++) {
      if (!cuts[wi]) continue;
      const P = W[wi].pts;
      for (let k = c0[wi]; k + 1 < c0[wi + 1]; k++) {
        let len = 0, up = 0, dn = 0;
        for (let i = cutPi[k]; i < cutPi[k + 1]; i++) {
          const a = P[i], b = P[i + 1], dx = b[0] - a[0], dz = b[2] - a[2], dy = b[1] - a[1];
          len += Math.sqrt(dx * dx + dz * dz);
          if (dy > 0) up += dy; else dn -= dy;
        }
        segFw[k] = walkTimeH(len, up, dn) * 60; segBw[k] = walkTimeH(len, dn, up) * 60;
        arc(root[k], root[k + 1], segFw[k]); arc(root[k + 1], root[k], segBw[k]);
      }
      if ((work += P.length) > STEP) { work = 0; yield; }
    }
    // a run's pieces either side of a deck / bore (graphInput splits there)
    for (let wi = 0; wi + 1 < n; wi++) {
      if (!cuts[wi] || !cuts[wi + 1] || W[wi].run < 0 || W[wi + 1].run !== W[wi].run) continue;
      const a = W[wi].pts[W[wi].pts.length - 1], b = W[wi + 1].pts[0];
      const d = Math.hypot(b[0] - a[0], b[2] - a[2]), dy = b[1] - a[1];
      if (d > DEST.gap_max_m) continue;
      const u = root[c0[wi + 1] - 1], v = root[c0[wi + 1]];
      arc(u, v, walkTimeH(d, Math.max(0, dy), Math.max(0, -dy)) * 60);
      arc(v, u, walkTimeH(d, Math.max(0, -dy), Math.max(0, dy)) * 60);
    }
    this.segFw = segFw; this.segBw = segBw;
    // a chain node (two neighbours: a run continued by the next one, no
    // junction) that no post / destination pins is contracted away — the
    // searches visit junctions, ends and the pinned cuts only. A start's
    // neighbouring cuts on its way stay (its first edge is stepped per way).
    const pin = new Uint8Array(N);
    for (const [wi, pi] of want) {
      const k = cuts[wi] ? this.cutAt(wi, pi) : -1;
      if (k < 0) continue;
      pin[root[k]] = 1;
      if (k > c0[wi]) pin[root[k - 1]] = 1;
      if (k + 1 < c0[wi + 1]) pin[root[k + 1]] = 1;
    }
    yield;
    const adj = Array.from({ length: N }, () => new Map());   // u -> Map(v -> least minutes)
    const setMin = (m, v, w) => { const o = m.get(v); if (o === undefined || w < o) m.set(v, w); };
    for (let e = 0; e < A.length; e++) setMin(adj[A[e]], B[e], C[e]);
    yield;
    const alive = new Uint8Array(N).fill(1);
    for (let again = true; again;) {
      again = false;
      for (let u = 0; u < N; u++) {   // id order: deterministic
        if ((u & 4095) === 4095) yield;
        if (!alive[u] || pin[u] || adj[u].size !== 2) continue;
        const [a, b] = adj[u].keys();
        if (!adj[a].has(u) || !adj[b].has(u)) continue;
        const ab = adj[a].get(u) + adj[u].get(b), ba = adj[b].get(u) + adj[u].get(a);
        adj[a].delete(u); adj[b].delete(u);
        setMin(adj[a], b, ab); setMin(adj[b], a, ba);
        adj[u].clear(); alive[u] = 0; again = true;
      }
      yield;
    }
    const nid = new Int32Array(N).fill(-1);
    let N2 = 0;
    for (let u = 0; u < N; u++) if (alive[u]) nid[u] = N2++;
    for (let k = 0; k < root.length; k++) root[k] = nid[root[k]];
    this.N = N = N2;
    const off = new Int32Array(N + 1);
    let E = 0;
    for (let u = 0; u < nid.length; u++) if (nid[u] >= 0) { off[nid[u] + 1] = adj[u].size; E += adj[u].size; }
    for (let i = 0; i < N; i++) off[i + 1] += off[i];
    const to = new Int32Array(E), wt = new Float32Array(E);
    for (let u = 0; u < nid.length; u++) {
      if (nid[u] < 0) continue;
      let s = off[nid[u]];
      for (const [v, w] of adj[u]) { to[s] = nid[v]; wt[s] = w; s++; }
    }
    this.off = off; this.to = to; this.wt = wt; this.edges = E;
    // search state (reused by every search)
    this.dist = new Float64Array(N); this.lab = new Int16Array(N);
    this.seen = new Int32Array(N); this.done = new Int32Array(N); this.stamp = 0;
    this.hk = new Float64Array(1024); this.hv = new Int32Array(1024); this.hn = 0;
  }
  // flat cut index of (way, vertex) | -1
  cutAt(wi, pi) {
    let lo = this.c0[wi], hi = this.c0[wi + 1] - 1;
    while (lo <= hi) {
      const m = (lo + hi) >> 1, v = this.cutPi[m];
      if (v === pi) return m;
      if (v < pi) lo = m + 1; else hi = m - 1;
    }
    return -1;
  }
  node(wi, pi) { const k = this.cutAt(wi, pi); return k < 0 ? -1 : this.nodeOfCut[k]; }
  _push(v, d) {
    if (this.hn === this.hk.length) {
      const k = new Float64Array(this.hn * 2), w = new Int32Array(this.hn * 2);
      k.set(this.hk); w.set(this.hv); this.hk = k; this.hv = w;
    }
    const hk = this.hk, hv = this.hv;
    let i = this.hn++;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (hk[p] < d || (hk[p] === d && hv[p] <= v)) break;
      hk[i] = hk[p]; hv[i] = hv[p]; i = p;
    }
    hk[i] = d; hv[i] = v;
  }
  _pop() {
    const hk = this.hk, hv = this.hv, v0 = hv[0], n = --this.hn;
    const d = hk[n], v = hv[n];
    let i = 0;
    for (;;) {
      let c = 2 * i + 1;
      if (c >= n) break;
      if (c + 1 < n && (hk[c + 1] < hk[c] || (hk[c + 1] === hk[c] && hv[c + 1] < hv[c]))) c++;
      if (hk[c] > d || (hk[c] === d && hv[c] >= v)) break;
      hk[i] = hk[c]; hv[i] = hv[c]; i = c;
    }
    hk[i] = d; hv[i] = v;
    return v0;
  }
  _relax(v, d, l) {
    const s = this.stamp;
    if (this.done[v] === s) return;
    if (this.seen[v] === s && this.dist[v] <= d) return;
    this.seen[v] = s; this.dist[v] = d; this.lab[v] = l;
    this._push(v, d);
  }
  // one Dijkstra (DIN minutes) from a guidepost: starts = [[wi, pi, dir,
  // label]…] — the post's own vertices are settled at 0 (a walk never comes
  // back through the post), each start's FIRST edge (along `dir`) carries
  // its label (the blade) to everything reached through it. → reached(node)
  search(starts, maxMin) {
    const s = ++this.stamp;
    this.hn = 0;
    for (const [wi, pi] of starts) {
      const u = this.node(wi, pi);
      if (u < 0) continue;
      this.seen[u] = s; this.done[u] = s; this.dist[u] = 0; this.lab[u] = -2;
    }
    for (const [wi, pi, dir, l] of starts) {
      const k = this.cutAt(wi, pi);
      if (k < 0) continue;
      const k2 = k + dir;
      if (k2 < this.c0[wi] || k2 >= this.c0[wi + 1]) continue;
      this._relax(this.nodeOfCut[k2], dir > 0 ? this.segFw[k] : this.segBw[k2], l);
    }
    const done = this.done, seen = this.seen, dist = this.dist, lab = this.lab, off = this.off, to = this.to, wt = this.wt;
    while (this.hn) {
      const u = this._pop();
      if (done[u] === s) continue;
      done[u] = s;
      const du = dist[u];
      if (du > maxMin) break;
      const l = lab[u];
      for (let e = off[u], e1 = off[u + 1]; e < e1; e++) {
        const v = to[e], d = du + wt[e];
        if (done[v] === s || (seen[v] === s && dist[v] <= d)) continue;
        seen[v] = s; dist[v] = d; lab[v] = l;
        this._push(v, d);
      }
    }
  }
  reached(v) { return v >= 0 && this.done[v] === this.stamp ? this.dist[v] : -1; }
}

// one blade's lines from its candidates [{name, key, grp, min, imp}] (already
// deduplicated across the post): the nearest ≥ min_min; the final = the major
// (weight ≥ 3) ≥ sep_min past it with the best weight − min / decay_min (then
// the farther); a mid between them when the gap allows (heaviest, then
// nearest the middle); ≤ max_min; no two lines of one settlement group;
// nearest on top
export function pickLines(cands) {
  const C = cands.filter((c) => c.min >= DEST.min_min && c.min <= DEST.max_min)
    .sort((a, b) => a.min - b.min || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  if (!C.length) return [];
  const near = C[0];
  let fin = null;
  for (const c of C) {
    if (c === near || c.imp < IMP_MAJOR || c.min < near.min + DEST.sep_min || c.grp === near.grp) continue;
    const sc = c.imp - c.min / DEST.decay_min, sf = fin ? fin.imp - fin.min / DEST.decay_min : -Infinity;
    if (sc > sf || (sc === sf && c.min > fin.min)) fin = c;
  }
  let mid = null;
  if (fin && fin.min - near.min >= 2 * DEST.sep_min) {
    const half = (near.min + fin.min) / 2;
    for (const c of C) {
      if (c === near || c === fin || c.min < near.min + DEST.sep_min || c.min > fin.min - DEST.sep_min || c.grp === near.grp || c.grp === fin.grp) continue;
      if (!mid || c.imp > mid.imp || (c.imp === mid.imp && Math.abs(c.min - half) < Math.abs(mid.min - half))) mid = c;
    }
  }
  return [near, mid, fin].filter(Boolean).slice(0, DEST.max_lines);
}

// every post's blades get `lines` = [[name, "1 h 30 min", minutes]…] (empty:
// the run name stays). plan = [{gp, blades, lost}] (bladesAt + the cuts);
// opts {pois, routes}. A job of steps (candidates, the walk net, then one
// post per step) run to the end here, or sliced over timers by
// planDestinationsAsync (TrailMarks.build: no long task at load). → stats
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
export function planDestinations(graph, plan, opts = {}) {
  const st = destStats();
  const job = destJob(graph, plan, opts || {}, st);
  while (!job.next().done);
  return st;
}
// the same job in timer slices of ~sliceMs (every step is ≤ ~4 ms: way /
// vertex chunks, the walk net's phases, one post's search) → Promise(stats)
export function planDestinationsAsync(graph, plan, opts = {}, sliceMs = SLICE_MS) {
  const st = destStats();
  return runSliced(destJob(graph, plan, opts || {}, st), sliceMs, () => { st.slices++; }).then(() => st);
}
const SLICE_MS = 6;
// drive a generator in setTimeout slices: steps until sliceMs is spent, then
// the next timer (input, XR frames and rendering run in between) → Promise(its
// return value)
function runSliced(job, sliceMs, onSlice) {
  return new Promise((resolve, reject) => {
    const run = () => {
      try {
        const t = now();
        for (;;) {
          const r = job.next();
          if (r.done) { resolve(r.value); return; }
          if (now() - t > sliceMs) break;
        }
        if (onSlice) onSlice();
        setTimeout(run, 0);
      } catch (e) { reject(e); }
    };
    setTimeout(run, 0);
  });
}
const destStats = () => ({ candidates: 0, attached: 0, valley_dropped: 0, nodes: 0, edges: 0, blades_dest: 0, lines: 0, ms: 0, ms_net: 0, slices: 1 });
function* destJob(graph, plan, opts, st) {
  let t0 = now();
  const lap = () => { const t = now(); st.ms += t - t0; t0 = t; };
  // a sub-job stepped through this one (its CPU time counted, not the waits)
  const drive = function* (job) {
    for (;;) {
      const r = job.next();
      if (r.done) return r.value;
      lap(); yield; t0 = now();
    }
  };
  for (const q of plan) for (const b of q.blades) b.lines = [];
  if (!graph || !plan.length) return;
  const gps = plan.map((q) => q.gp);
  // the walk network's bbox + a coarse valley grid (lowest vertex y per
  // valley_cell_m cell), in way slices
  let bbox = null;
  const VC = DEST.valley_cell_m, low = new Map();   // cell key -> min y
  const cellKey = (cx, cz) => (cx + 32768) * 65536 + (cz + 32768);
  for (let wi = 0, nv = 0; wi < graph.ways.length; wi++) {
    const w = graph.ways[wi];
    if (w.kind === 'rail') continue;
    for (const p of w.pts) {
      if (!bbox) bbox = [p[0], p[2], p[0], p[2]];
      if (p[0] < bbox[0]) bbox[0] = p[0]; if (p[0] > bbox[2]) bbox[2] = p[0];
      if (p[2] < bbox[1]) bbox[1] = p[2]; if (p[2] > bbox[3]) bbox[3] = p[2];
      const k = cellKey(Math.floor(p[0] / VC), Math.floor(p[2] / VC)), o = low.get(k);
      if (o === undefined || p[1] < o) low.set(k, p[1]);
    }
    if ((nv += w.pts.length) > 15000) { nv = 0; lap(); yield; t0 = now(); }
  }
  // the valley under (x, z): the lowest walk vertex in the cells that touch
  // the valley_r_m circle (≈ 3 km, cell-exact)
  const valleyAt = (x, z) => {
    const R = DEST.valley_r_m, c0 = Math.floor((x - R) / VC), c1 = Math.floor((x + R) / VC);
    const r0 = Math.floor((z - R) / VC), r1 = Math.floor((z + R) / VC);
    let m = Infinity;
    for (let cx = c0; cx <= c1; cx++) for (let cz = r0; cz <= r1; cz++) {
      const ex = Math.max(cx * VC - x, 0, x - (cx + 1) * VC), ez = Math.max(cz * VC - z, 0, z - (cz + 1) * VC);
      if (ex * ex + ez * ez > R * R) continue;
      const o = low.get(cellKey(cx, cz));
      if (o !== undefined && o < m) m = o;
    }
    return m;
  };
  const cands = yield* drive(destCandidatesJob(gps, opts.pois, opts.routes, bbox));
  st.candidates = cands.length;
  lap(); yield; t0 = now();
  // attach every candidate to its nearest walkable vertex; a restaurant /
  // hotel (iso) only on a MOUNTAIN: its ele (else the attach vertex's y)
  // ≥ the valley + mountain_m — a valley-floor restaurant is no destination
  const want = [];
  let nc = 0;
  for (const c of cands) {
    if ((++nc & 63) === 0) { lap(); yield; t0 = now(); }
    let b = null;
    const y = Number.isFinite(c.y) ? c.y : 0, dy = Number.isFinite(c.y) ? c.dy : 1e6;
    graph.near(c.x, y, c.z, c.r, dy, (wi, pi, d2) => {
      if (graph.ways[wi].kind === 'rail') return;
      if (!b || d2 < b.d2 || (d2 === b.d2 && (wi < b.wi || (wi === b.wi && pi < b.pi)))) b = { wi, pi, d2 };
    });
    if (!b) continue;
    if (c.iso) {
      const yc = Number.isFinite(c.y) ? c.y : graph.ways[b.wi].pts[b.pi][1];
      if (yc < valleyAt(c.x, c.z) + DEST.mountain_m) { st.valley_dropped++; continue; }
    }
    c.wi = b.wi; c.pi = b.pi; c.off = Math.sqrt(b.d2); want.push([b.wi, b.pi]); st.attached++;
  }
  for (const q of plan) for (const b of [...q.blades, ...q.lost]) for (const s of b.starts) want.push([s[0], s[1]]);
  lap();
  yield;
  t0 = now();
  // the walk net, its build stepped too (no one long task)
  const ms0 = st.ms;
  const net = new WalkNet(graph, want, true);
  yield* drive(net.job);
  lap();
  st.nodes = net.N; st.edges = net.edges;
  st.ms_net = Math.round(st.ms - ms0);
  for (const c of cands) c.node = c.wi === undefined ? -1 : net.node(c.wi, c.pi);
  lap();
  yield;
  t0 = now();
  for (const q of plan) {
    if (!q.blades.length) continue;
    const starts = [];
    q.blades.forEach((b, k) => { for (const s of b.starts) starts.push([s[0], s[1], s[2], k]); });
    for (const b of q.lost) for (const s of b.starts) starts.push([s[0], s[1], s[2], -1]);
    net.search(starts, DEST.max_min + 1);
    const ok0 = keyOf(cleanName(q.gp.name));
    const own = [ok0, ...ok0.split('/').map((s) => s.trim())].filter(Boolean);   // "Celerina/Schlarigna": each part is here
    // one entry per name (no destination twice on a post): its fastest
    // instance decides the blade (ties: heavier, then the lower blade); the
    // weight is the heaviest instance reached through that same blade
    const hits = [];
    for (const c of cands) {
      const d = net.reached(c.node);
      if (d < 0 || own.includes(c.key) || Math.hypot(c.x - q.gp.x, c.z - q.gp.z) < DEST.here_m) continue;
      const k = net.lab[c.node];
      if (k < 0) continue;
      if (c.src === 'route' && !(q.blades[k].t.routes || []).includes(c.ri)) continue;
      hits.push({ name: c.name, key: c.key, grp: c.grp, min: d + (c.off / 1000 / 4) * 60, imp: c.imp, k });
    }
    hits.sort((a, b) => a.min - b.min || b.imp - a.imp || a.k - b.k || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    const best = new Map();   // name key -> the fastest hit
    for (const h of hits) {
      const o = best.get(h.key);
      if (!o) best.set(h.key, { ...h });
      else if (h.k === o.k && h.imp > o.imp) o.imp = h.imp;
    }
    const per = q.blades.map(() => []);
    for (const c of best.values()) per[c.k].push(c);
    q.blades.forEach((b, k) => {
      b.lines = pickLines(per[k]).map((c) => [c.name, fmtWalkMin(c.min), Math.round(c.min * 10) / 10]);
      if (b.lines.length) { st.blades_dest++; st.lines += b.lines.length; }
    });
    lap();
    yield;
    t0 = now();
  }
  st.ms = Math.round(st.ms);
}

// ------------------------------------------------------------- the atlas
// NEAR-ONLY (BLADES-T): ONE CanvasTexture atlas_w × atlas_h of slot_w ×
// slot_h slots (3 × 12; slot 0 solid white = the uv of every untextured face,
// the vertex colour alone paints it). Only the posts within near_m of the
// camera hold slots (NearSlots); a blade far away is plain (body colour + tip
// bands), exactly the untextured face. A blade's body maps onto the top of its
// slot at ppm = slot_w / blade_l_m texels per metre.
const ATLAS_COLS = () => Math.floor(SIGN.atlas_w / SIGN.slot_w);
export function slotRect(s, wpx, hpx, out = [0, 0, 0, 0]) {
  const C = ATLAS_COLS(), c = s % C, r = Math.floor(s / C);
  const x0 = c * SIGN.slot_w + 2, x1 = c * SIGN.slot_w + Math.min(SIGN.slot_w, wpx) - 2;
  const y0 = r * SIGN.slot_h + 2, y1 = r * SIGN.slot_h + Math.min(SIGN.slot_h, hpx) - 2;
  out[0] = x0 / SIGN.atlas_w; out[1] = 1 - y1 / SIGN.atlas_h; out[2] = x1 / SIGN.atlas_w; out[3] = 1 - y0 / SIGN.atlas_h;   // flipY texture: v up
  return out;
}
const RECT = [0, 0, 0, 0];   // _flipPost's scratch rect (no allocation per flip)
const WHITE_UV = [SIGN.slot_w / 2 / SIGN.atlas_w, 1 - SIGN.slot_h / 2 / SIGN.atlas_h];

// Which posts hold atlas slots (pure, no DOM; per frame, no allocation).
// X / Z: post positions, need[i]: its text items (0 = never textured), cap:
// the atlas slots (slot 0 white is never handed out). frame(x, z, paint, flip):
//   · slots released last frame become free (their faces went plain a frame
//     before the slot can be repainted — no stale text on a far blade),
//   · when the camera moved > move_m (or a post waits for slots): posts past
//     far_m release theirs (flip(i, false)); posts within near_m, nearest
//     first, get one slot per item and queue their items,
//   · ONE queued item is painted (paint(item, slot)); a post whose items are
//     all painted is shown (flip(i, true)).
export class NearSlots {
  constructor(X, Z, need, cap, opt = {}) {
    const n = X.length;
    this.X = X; this.Z = Z; this.need = need; this.n = n;
    this.rIn2 = (opt.near_m ?? SIGN.near_m) ** 2; this.rOut2 = (opt.far_m ?? SIGN.far_m) ** 2; this.move2 = (opt.move_m ?? SIGN.move_m) ** 2;
    this.item0 = new Int32Array(n + 1);
    for (let i = 0; i < n; i++) this.item0[i + 1] = this.item0[i] + need[i];
    const items = this.item0[n];
    this.itemPost = new Int32Array(items);
    for (let i = 0; i < n; i++) for (let k = this.item0[i]; k < this.item0[i + 1]; k++) this.itemPost[k] = i;
    this.slot = new Int16Array(items).fill(-1);
    this.state = new Uint8Array(n);   // 0 plain, 1 painting, 2 shown
    this.left = new Int32Array(n);
    this.free = new Int16Array(cap); this.nFree = 0;
    for (let s = cap - 1; s >= 1; s--) this.free[this.nFree++] = s;   // pop → lowest slot first
    this.cool = new Int16Array(cap); this.nCool = 0;
    this.q = new Int32Array(Math.max(1, items)); this.qn = 0;
    this.lx = NaN; this.lz = NaN; this.retry = false;
    this.painted = 0; this.evals = 0;
  }
  get shown() { let c = 0; for (let i = 0; i < this.n; i++) if (this.state[i] === 2) c++; return c; }
  frame(x, z, paint, flip) {
    while (this.nCool) this.free[this.nFree++] = this.cool[--this.nCool];
    const dx = x - this.lx, dz = z - this.lz;
    if (!(dx * dx + dz * dz <= this.move2) || this.retry) { this.lx = x; this.lz = z; this._eval(x, z, flip); }
    if (!this.qn) return 0;
    const it = this.q[0];
    this.q.copyWithin(0, 1, this.qn--);
    paint(it, this.slot[it]);
    this.painted++;
    const p = this.itemPost[it];
    if (--this.left[p] === 0) { this.state[p] = 2; flip(p, true); }
    return 1;
  }
  _release(i, flip) {
    if (this.state[i] === 2) flip(i, false);
    if (this.state[i] === 1) {   // drop its queued items
      let w = 0;
      for (let r = 0; r < this.qn; r++) if (this.itemPost[this.q[r]] !== i) this.q[w++] = this.q[r];
      this.qn = w;
    }
    for (let k = this.item0[i]; k < this.item0[i + 1]; k++) { this.cool[this.nCool++] = this.slot[k]; this.slot[k] = -1; }
    this.state[i] = 0;
  }
  _eval(x, z, flip) {
    this.evals++;
    this.retry = false;
    for (let i = 0; i < this.n; i++) {
      if (!this.state[i]) continue;
      const dx = this.X[i] - x, dz = this.Z[i] - z;
      if (dx * dx + dz * dz > this.rOut2) this._release(i, flip);
    }
    for (;;) {
      let bi = -1, bd = this.rIn2;
      for (let i = 0; i < this.n; i++) {
        if (this.state[i] || !this.need[i]) continue;
        const dx = this.X[i] - x, dz = this.Z[i] - z, d = dx * dx + dz * dz;
        if (d <= bd && (bi < 0 || d < bd)) { bd = d; bi = i; }
      }
      if (bi < 0) return;
      if (this.need[bi] > this.nFree) { this.retry = this.nCool > 0; return; }
      for (let k = this.item0[bi]; k < this.item0[bi + 1]; k++) { this.slot[k] = this.free[--this.nFree]; this.q[this.qn++] = k; }
      this.left[bi] = this.need[bi];
      this.state[bi] = 1;
    }
  }
}

// the canvas side: one slot painted per call (≤ 7 canvas ops), fonts fixed
const FONT_FAMILY = 'system-ui, "Helvetica Neue", Arial, sans-serif';
const STY = { y: [MARK_COLOUR.yellow, '#111111'], b: [MARK_COLOUR.blue, '#ffffff'], p: [MARK_COLOUR.yellow, '#111111'] };
class NearAtlas {
  constructor() {
    this.tex = null;
    if (typeof document === 'undefined') return;   // node probe: geometry only
    const cv = document.createElement('canvas');
    cv.width = SIGN.atlas_w; cv.height = SIGN.atlas_h;
    this.g = cv.getContext('2d');
    this.g.fillStyle = '#ffffff'; this.g.fillRect(0, 0, SIGN.slot_w, SIGN.slot_h);
    const ppm = SIGN.slot_w / SIGN.blade_l_m;
    this.font1 = `700 ${Math.round(SIGN.blade_h_m * ppm * 0.54)}px ${FONT_FAMILY}`;   // one line: the run name
    this.fontL = `700 ${Math.round(SIGN.line_m * ppm * 0.8)}px ${FONT_FAMILY}`;        // destination lines
    this.fontP = `700 ${Math.round(SIGN.plate_h_m * ppm * 0.6)}px ${FONT_FAMILY}`;     // the location plate
    this.pitch = SIGN.line_m * ppm;
    this.timeW = Math.round(SIGN.slot_w * 0.32);
    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    this.tex = tex;
  }
  // style 'y'|'b'|'p', lines [[name, time]…] or [[text]], the body's texel size
  paint(slot, style, lines, wpx, hpx) {
    const g = this.g;
    if (!g) return;
    const C = ATLAS_COLS(), x = (slot % C) * SIGN.slot_w, y = Math.floor(slot / C) * SIGN.slot_h;
    const st = STY[style] || STY.y, W = Math.min(SIGN.slot_w, wpx);
    g.fillStyle = st[0]; g.fillRect(x, y, SIGN.slot_w, SIGN.slot_h);
    g.fillStyle = st[1]; g.textBaseline = 'middle';
    if (lines[0].length === 1) {
      g.font = style === 'p' ? this.fontP : this.font1; g.textAlign = 'left';
      g.fillText(lines[0][0], x + 10, y + hpx / 2 + 1, W - 20);
      return;
    }
    g.font = this.fontL;
    const n = lines.length, top = y + (hpx - n * this.pitch) / 2 + this.pitch / 2 + 1;
    for (let k = 0; k < n; k++) {
      const yk = top + k * this.pitch;
      g.textAlign = 'left'; g.fillText(lines[k][0], x + 8, yk, W - 24 - this.timeW);
      g.textAlign = 'right'; g.fillText(lines[k][1], x + W - 8, yk, this.timeW);
    }
  }
}

// incident graded runs of one guidepost -> blades [{d:[dx, dz], t (trail),
// starts:[[wi, pi, dir]…]}] in bearing order (all of them; the caller keeps
// max_blades). A blade merged into another (same name + mark within
// merge_deg) hands it its start, so its way still leads to that blade.
export function bladesAt(gp, graph, byRun) {
  const near = new Map();   // run -> {wi, pi, d2}
  graph.near(gp.x, gp.y, gp.z, SIGN.radius_m, 25, (wi, pi, d2) => {
    const run = graph.ways[wi].run, t = byRun.get(run);
    if (!t || t.mark === 'none') return;
    const b = near.get(run);
    if (!b || d2 < b.d2) near.set(run, { wi, pi, d2, t });
  });
  const out = [];
  for (const { wi, pi, t } of near.values()) {
    const P = graph.ways[wi].pts;
    for (const dir of [-1, 1]) {
      let j = pi, s = 0;
      while (j + dir >= 0 && j + dir < P.length && s < SIGN.reach_m) {
        s += Math.hypot(P[j + dir][0] - P[j][0], P[j + dir][2] - P[j][2]);
        j += dir;
      }
      if (s < SIGN.min_m) continue;
      const dx = P[j][0] - P[pi][0], dz = P[j][2] - P[pi][2], L = Math.hypot(dx, dz);
      if (L < 1e-3) continue;
      out.push({ d: [dx / L, dz / L], t, starts: [[wi, pi, dir]] });
    }
  }
  // one blade per (direction, run text + mark) within merge_deg; bearing order
  const cosM = Math.cos(SIGN.merge_deg * Math.PI / 180);
  const kept = [];
  for (const b of out) {
    const k = kept.find((k) => k.t.mark === b.t.mark && k.t.name === b.t.name && k.d[0] * b.d[0] + k.d[1] * b.d[1] > cosM);
    if (k) { k.starts.push(...b.starts); continue; }
    kept.push(b);
  }
  kept.sort((a, b) => Math.atan2(a.d[0], -a.d[1]) - Math.atan2(b.d[0], -b.d[1]));
  return kept;
}

// a blade's body height: 1–2 lines the TRAILS-C blade, 3 lines taller
const bladeH = (b) => (b.lines && b.lines.length >= 3 ? SIGN.blade_h3_m : SIGN.blade_h_m);

// one blade on the signpost centred at yc: body (white slot uv + body colour
// until a near slot is flipped in), tip bands in the mark colours, the arrow
// point; both faces (u mirrored behind: the text reads left -> right).
// → the body item {va, vb (the two faces' first vertex), sa, sb (their face
// shade), body}
function bladeGeo(A, gp, b, yc) {
  const S = SIGN, d = b.d, n = [-d[1], d[0]], m = b.t.mark;
  const body = m === 'wbw' ? COL.blue : COL.yellow;
  const L = S.blade_l_m, hh = bladeH(b) / 2, r0 = S.pole_half_m;
  const tip = m === 'yellow' ? [] : m === 'ferrata' ? [COL.blue, COL.white] : MARK[m].bands.map((k) => COL[k]);
  const it = { va: 0, vb: 0, sa: 1, sb: 1, body };
  for (const side of [1, -1]) {
    const o = side * 0.012;
    const P = (s, y) => [gp.x + d[0] * (r0 + s) + n[0] * o, y, gp.z + d[1] * (r0 + s) + n[1] * o];
    const nn = [n[0] * side, 0, n[1] * side];
    const v = A.poly([P(0, yc - hh), P(L, yc - hh), P(L, yc + hh), P(0, yc + hh)], nn, body);
    if (side > 0) { it.va = v; it.sa = shadeS(nn); } else { it.vb = v; it.sb = shadeS(nn); }
    let s1 = L;
    if (tip.length) {
      const bh = (2 * hh) / tip.length;
      tip.forEach((c, k) => {
        const yt = yc + hh - k * bh;
        A.poly([P(L, yt - bh), P(L + S.tip_m, yt - bh), P(L + S.tip_m, yt), P(L, yt)], nn, c);
      });
      s1 = L + S.tip_m;
    }
    A.poly([P(s1, yc - hh), P(s1 + S.point_m, yc), P(s1, yc + hh)], nn, tip.length ? tip[tip.length - 1] : body);
  }
  return it;
}
const shadeS = (n) => 0.68 + 0.3 * Math.max(0, n[1]) + 0.09 * n[0] + 0.05 * n[2];

// GPU bytes of a built geometry (attributes + index)
function geoBytes(g) {
  let b = g.index ? g.index.array.byteLength : 0;
  for (const a of Object.values(g.attributes)) b += a.array.byteLength;
  return b;
}

// ----------------------------------------------------------------- class
export class TrailMarks {
  constructor(scene) {
    this.scene = scene;
    this.group = new THREE.Group();
    this.group.name = 'trail-marks';
    this.group.matrixAutoUpdate = false;
    this.stats = null;
    this.near = null;       // NearSlots, once the signposts are built
    this.ready = Promise.resolve(null);   // settles when the signposts are built
    this._frame = -1;
  }

  // trails / byRun: WaysNet.trails / trailByRun; graph: WaysNet.graph;
  // guideposts: the parsed trails/guideposts.json (or null); opts (each
  // optional — absent: the destinations come from the named guideposts
  // alone): {pois: gazetteer pois, routes: prepRoutes' list (trail.routes
  // indexes it), sync: true = the signposts now (node probes)}. The stakes
  // are built here; the signposts once their destinations are derived (in
  // timer slices — `ready`; the mesh, its material and texture exist from
  // here, so warmLate compiles the program). Built once; drops the graded
  // runs' station arrays afterwards. buildSteps = THE build (M7c: the
  // stakes' plan + geometry were one 60-80 ms task after the Beam): a step
  // per 16 graded runs (planPostsSteps), 256 stakes, 32 guideposts; the
  // meshes join the group, the stations are dropped and the group joins the
  // scene in the last step, one task; build = its drain
  build(trails, byRun, graph, guideposts, opts = {}) {
    const it = this.buildSteps(trails, byRun, graph, guideposts, opts);
    for (;;) { const r = it.next(); if (r.done) return r.value; }
  }
  *buildSteps(trails, byRun, graph, guideposts, opts = {}) {
    opts = opts || {};
    const st = { posts: 0, post_tris: 0, post_draws: 0, post_bytes: 0, spacing_scale: 1, signposts: 0, blades: 0,
      blades_dest: 0, blades_name: 0, blades_plain: 0, blades_cut: 0, plates: 0, sign_tris: 0, sign_draws: 0, sign_bytes: 0,
      atlas_slots: 0, atlas_texels: '', text_items: 0, dest: null };
    // marker stakes
    const { posts, spacing_scale } = yield* planPostsSteps(trails);
    st.spacing_scale = Math.round(spacing_scale * 100) / 100;
    let postMesh = null;
    if (posts.length) {
      const A = new Acc(false);
      for (let i = 0; i < posts.length; i++) {
        if (i && i % 256 === 0) yield;
        postGeo(A, posts[i]);
      }
      yield;
      const mesh = new THREE.Mesh(A.geometry(), new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide, fog: true }));
      mesh.matrixAutoUpdate = false;
      mesh.name = 'trail-posts';
      postMesh = mesh;
      st.posts = posts.length; st.post_tris = A.tris; st.post_draws = 1; st.post_bytes = geoBytes(mesh.geometry);
    }
    // signposts at the OSM guidepost nodes
    const gps = guideposts && Array.isArray(guideposts.posts) ? guideposts.posts : [];
    let sign = null;
    if (gps.length && graph) {
      const plan = [];
      for (let i = 0; i < gps.length; i++) {
        if (i && i % 32 === 0) yield;
        const gp = gps[i];
        if (!Number.isFinite(gp.x) || !Number.isFinite(gp.z) || !Number.isFinite(gp.y)) continue;
        const all = bladesAt(gp, graph, byRun);
        const loc = [gp.name || '', Number.isFinite(gp.ele) ? `${Math.round(gp.ele)} m` : ''].filter(Boolean).join(' · ');
        plan.push({ gp, blades: all.slice(0, SIGN.max_blades), lost: all.slice(SIGN.max_blades), loc });
      }
      // tri budget: past SIGN.tri_budget the busiest posts lose their last
      // blades first (pole 10 + plate 4 + blade <= 18 tris)
      const est = () => plan.reduce((a, q) => a + 10 + (q.loc ? 4 : 0) + 18 * q.blades.length, 0);
      for (let cap = SIGN.max_blades - 1; cap >= 1 && est() > SIGN.tri_budget; cap--) {
        for (const q of plan) if (q.blades.length > cap) { st.blades_cut += q.blades.length - cap; q.lost.unshift(...q.blades.splice(cap)); }
      }
      // the mesh now (a one-triangle stand-in), its geometry once the
      // destinations are known
      const atlas = new NearAtlas();
      const g0 = new THREE.BufferGeometry();
      const p0 = plan.length ? plan[0].gp : { x: 0, y: 0, z: 0 };
      g0.setAttribute('position', new THREE.Float32BufferAttribute([p0.x, p0.y, p0.z, p0.x, p0.y, p0.z, p0.x, p0.y, p0.z], 3));
      g0.setAttribute('color', new THREE.Float32BufferAttribute(new Array(9).fill(1), 3));
      g0.setAttribute('uv', new THREE.Float32BufferAttribute([...WHITE_UV, ...WHITE_UV, ...WHITE_UV], 2));
      const mesh = new THREE.Mesh(g0, new THREE.MeshBasicMaterial({ vertexColors: true, map: atlas.tex, side: THREE.DoubleSide, fog: true }));
      mesh.matrixAutoUpdate = false;
      mesh.name = 'trail-signposts';
      // per frame, from the render itself (VR + flat + phone alike; no
      // main.js hook): the camera position drives the near set, once a frame
      mesh.onBeforeRender = (renderer, scene, camera) => {
        const f = renderer.info.render.frame;
        if (f === this._frame) return;
        this._frame = f;
        const e = camera.matrixWorld.elements;
        this.update(e[12], e[14]);
      };
      sign = { mesh, atlas, plan };
    }
    // the commit, one task: the meshes, the stations dropped, the scene
    if (postMesh) this.group.add(postMesh);
    for (const t of trails) delete t.st;   // placed: the index stays slim
    if (sign) {
      this.group.add(sign.mesh);
      this._mesh = sign.mesh; this._atlas = sign.atlas;
      st.sign_draws = 1;
      this._dp = { graph, plan: sign.plan, opts: { pois: opts.pois || null, routes: opts.routes || null }, sync: !!opts.sync, st };
      this._plan();
    }
    this.group.updateMatrixWorld(true);
    if (this.group.children.length && this.scene) this.scene.add(this.group);
    this.stats = st;
    return st;
  }

  // (re)derive the destinations, then (re)build the signposts; chained on
  // `ready`, so a late setPois never overlaps a running derivation
  _plan() {
    const { graph, plan, opts, sync, st } = this._dp;
    const fail = (e) => {
      console.warn('[trails] blade destinations skipped:', e);
      for (const q of plan) for (const b of q.blades) b.lines = [];
      return null;
    };
    if (sync) {
      let d = null;
      try { d = planDestinations(graph, plan, opts); } catch (e) { d = fail(e); }
      this._signs(plan, d, st);
      return;
    }
    this.ready = this.ready.then(() => planDestinationsAsync(graph, plan, opts)).catch(fail)
      .then((d) => runSliced(this._signsJob(plan, d, st), SLICE_MS))
      .then(() => {
        console.info('[trails] signposts', JSON.stringify(st));
        return st;
      });
  }

  // the gazetteer pois when they arrive AFTER build (build had pois null):
  // the destinations are derived again with them (sliced, in the
  // background) and the signposts swap in once more. No-op without
  // signposts, for null / the same list, or when build already had pois.
  // → `ready`
  setPois(pois) {
    const dp = this._dp;
    if (!dp || !Array.isArray(pois) || dp.opts.pois) return this.ready;
    dp.opts = { ...dp.opts, pois };
    this._plan();
    return this.ready;
  }

  // the signposts' geometry from the plan (blades with their lines): poles,
  // blades stacked from blade_top_m (a 3-line blade is taller), the location
  // plates; the text items (blade / plate faces) post by post for NearSlots
  _signs(plan, dest, st) {
    const job = this._signsJob(plan, dest, st);
    while (!job.next().done);
  }
  // the same, a yield every 32 posts; the swap (geometry + NearSlots) is
  // the last step, one task
  *_signsJob(plan, dest, st) {
    st.dest = dest;
    // a re-plan (setPois) counts afresh; the new geometry + NearSlots start
    // plain in this same task, so no frame mixes old faces and new slots
    Object.assign(st, { signposts: 0, blades: 0, blades_dest: 0, blades_name: 0, blades_plain: 0, plates: 0 });
    const A = new Acc(true);
    const items = [];   // {va, vb, sa, sb, body, style, lines, wpx, hpx}
    const X = [], Z = [], need = [];
    const ppm = SIGN.slot_w / SIGN.blade_l_m;
    for (const { gp, blades, loc } of plan) {
      const y = gp.y, n0 = items.length;
      const f0 = blades.length ? blades[0].d : [1, 0];
      prism(A, gp.x, gp.z, y - SIGN.sink_m, y + SIGN.pole_h_m, SIGN.pole_half_m, f0, COL.pole);
      let top = y + SIGN.blade_top_m + SIGN.blade_h_m / 2;
      for (const b of blades) {
        const h = bladeH(b);
        const it = bladeGeo(A, gp, b, top - h / 2);
        top -= h + SIGN.blade_gap_m;
        const style = b.t.mark === 'wbw' ? 'b' : 'y';
        if (b.lines && b.lines.length) { st.blades_dest++; items.push({ ...it, style, lines: b.lines.map((l) => [l[0], l[1]]), wpx: SIGN.slot_w, hpx: h * ppm }); }
        else if (b.t.name) { st.blades_name++; items.push({ ...it, style, lines: [[b.t.name]], wpx: SIGN.slot_w, hpx: h * ppm }); }
        else st.blades_plain++;
      }
      // the location plate (real data: the node's name / ele) on the pole
      // head, centred, along the first blade
      if (loc) {
        const hl = SIGN.plate_l_m / 2, hh = SIGN.plate_h_m / 2, yc = y + SIGN.pole_h_m - hh - 0.02;
        const n = [-f0[1], f0[0]];
        const it = { va: 0, vb: 0, sa: 1, sb: 1, body: COL.yellow };
        for (const side of [1, -1]) {
          const o = side * (SIGN.pole_half_m + 0.006);
          const P = (s, yy) => [gp.x + f0[0] * s + n[0] * o, yy, gp.z + f0[1] * s + n[1] * o];
          const nn = [n[0] * side, 0, n[1] * side];
          const v = A.poly([P(-hl, yc - hh), P(hl, yc - hh), P(hl, yc + hh), P(-hl, yc + hh)], nn, COL.yellow);
          if (side > 0) { it.va = v; it.sa = shadeS(nn); } else { it.vb = v; it.sb = shadeS(nn); }
        }
        items.push({ ...it, style: 'p', lines: [[loc]], wpx: SIGN.plate_l_m * ppm, hpx: SIGN.plate_h_m * ppm });
        st.plates++;
      }
      X.push(gp.x); Z.push(gp.z); need.push(items.length - n0);
      st.signposts++;
      st.blades += blades.length;
      if ((st.signposts & 31) === 0) yield;
    }
    yield;
    const geo = A.geometry();
    geo.attributes.uv.setUsage(THREE.DynamicDrawUsage);
    geo.attributes.color.setUsage(THREE.DynamicDrawUsage);
    const old = this._mesh.geometry;
    this._mesh.geometry = geo;
    old.dispose();
    const cap = ATLAS_COLS() * Math.floor(SIGN.atlas_h / SIGN.slot_h);
    this._items = items; this._geo = geo;
    this._paint = (k, slot) => { const it = items[k]; this._atlas.paint(slot, it.style, it.lines, it.wpx, it.hpx); };
    this._flip = (p, on) => this._flipPost(p, on);
    this.near = new NearSlots(Float64Array.from(X), Float64Array.from(Z), Int32Array.from(need), cap);
    Object.assign(st, { sign_tris: A.tris, sign_draws: 1, atlas_slots: cap - 1, text_items: items.length,
      atlas_texels: `${SIGN.atlas_w}x${SIGN.atlas_h}`, sign_bytes: geoBytes(geo) });
  }

  // the near-only texts: call once a frame with the camera's world x / z (the
  // signpost mesh's onBeforeRender does). ≤ 1 slot painted per call.
  update(x, z) {
    if (this.near) this.near.frame(x, z, this._paint, this._flip);
  }

  // a post's text faces: on → their slot's uv + white (the texture paints),
  // off → the white slot + the body colour (plain); then the touched range
  // is re-uploaded (the texture once, when a post is shown)
  _flipPost(p, on) {
    const N = this.near, uv = this._geo.attributes.uv, col = this._geo.attributes.color;
    const U = uv.array, Cc = col.array;
    let v0 = Infinity, v1 = -Infinity;
    for (let k = N.item0[p]; k < N.item0[p + 1]; k++) {
      const it = this._items[k];
      const R = on ? slotRect(N.slot[k], it.wpx, it.hpx, RECT) : null;
      for (let f = 0; f < 2; f++) {
        const v = f ? it.vb : it.va, s = f ? it.sb : it.sa;
        for (let j = 0; j < 4; j++) {
          const o = (v + j) * 2;
          if (R) {
            // front: u along the blade (0, L, L, 0); back mirrored
            const right = f ? (j === 0 || j === 3) : (j === 1 || j === 2);
            U[o] = right ? R[2] : R[0];
            U[o + 1] = j < 2 ? R[1] : R[3];
          } else { U[o] = WHITE_UV[0]; U[o + 1] = WHITE_UV[1]; }
          const c = (v + j) * 3;
          Cc[c] = (on ? 1 : it.body[0]) * s; Cc[c + 1] = (on ? 1 : it.body[1]) * s; Cc[c + 2] = (on ? 1 : it.body[2]) * s;
        }
        if (v < v0) v0 = v;
        if (v + 4 > v1) v1 = v + 4;
      }
    }
    if (v1 > v0) {
      uv.addUpdateRange(v0 * 2, (v1 - v0) * 2); uv.needsUpdate = true;
      col.addUpdateRange(v0 * 3, (v1 - v0) * 3); col.needsUpdate = true;
    }
    if (on && this._atlas.tex) this._atlas.tex.needsUpdate = true;
  }
}
